import React, { useState, useEffect, useMemo } from 'react';

import { tippspielAPI } from '../services/api';

import ScoringInfo from '../components/ScoringInfo';

import PageHeader from '../components/PageHeader';

import MatchTipCard from '../components/MatchTipCard';

import { useTippspielBranding } from '../hooks/useTippspielBranding';



function groupOpenRounds(matches) {

  const byRound = new Map();

  matches.filter((m) => m.canTip).forEach((m) => {

    const id = m.mfleagueRoundId || 'other';

    const name = m.mfleagueRoundName || 'Spieltag';

    if (!byRound.has(id)) {

      byRound.set(id, {

        id,

        name,

        matches: [],

        earliestKickoff: null,

        missingTips: 0,

      });

    }

    const group = byRound.get(id);

    group.matches.push(m);

    if (!m.myTip) group.missingTips += 1;

    if (m.kickoffAt) {

      const t = new Date(m.kickoffAt).getTime();

      if (!group.earliestKickoff || t < group.earliestKickoff) {

        group.earliestKickoff = t;

      }

    }

  });



  return Array.from(byRound.values())

    .sort((a, b) => (a.earliestKickoff || 0) - (b.earliestKickoff || 0))

    .map((g) => ({

      ...g,

      matches: [...g.matches].sort(

        (a, b) => new Date(a.kickoffAt || 0) - new Date(b.kickoffAt || 0)

      ),

    }));

}



const TippspielSubmit = () => {

  const { scoring } = useTippspielBranding();

  const [matches, setMatches] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');

  const [saving, setSaving] = useState({});

  const [messages, setMessages] = useState({});

  const [drafts, setDrafts] = useState({});

  const [openRoundId, setOpenRoundId] = useState(null);

  const [showScoring, setShowScoring] = useState(false);



  const loadMatches = () => tippspielAPI.getMatches({ tippableOnly: 'true' })

    .then((res) => {

      const list = res.data || [];

      setMatches(list);

      const d = {};

      list.forEach((m) => {

        if (m.myTip) {

          d[m._id] = { home: String(m.myTip.tipHome), away: String(m.myTip.tipAway) };

        } else {

          d[m._id] = { home: '', away: '' };

        }

      });

      setDrafts(d);

      return list;

    });



  useEffect(() => {

    setLoading(true);

    setError('');

    loadMatches()

      .catch((err) => setError(err.response?.data?.message || 'Spiele konnten nicht geladen werden.'))

      .finally(() => setLoading(false));

  }, []);



  const roundGroups = useMemo(() => groupOpenRounds(matches), [matches]);

  const openWithoutTip = matches.filter((m) => m.canTip && !m.myTip).length;



  useEffect(() => {

    if (!roundGroups.length) return;

    if (openRoundId && roundGroups.some((r) => r.id === openRoundId)) return;

    const firstMissing = roundGroups.find((r) => r.missingTips > 0);

    setOpenRoundId((firstMissing || roundGroups[0]).id);

  }, [roundGroups, openRoundId]);



  const isValidScore = (val) => {

    if (val === '') return false;

    const n = Number(val);

    return Number.isInteger(n) && n >= 0 && n <= 99;

  };



  const submit = async (matchId) => {

    const d = drafts[matchId];

    if (!isValidScore(d?.home) || !isValidScore(d?.away)) {

      setMessages((m) => ({ ...m, [matchId]: 'Bitte ganze Zahlen von 0–99 eingeben.' }));

      return;

    }

    setSaving((s) => ({ ...s, [matchId]: true }));

    setMessages((m) => ({ ...m, [matchId]: '' }));

    try {

      await tippspielAPI.submitTip({

        matchId,

        tipHome: Number(d.home),

        tipAway: Number(d.away),

      });

      setMessages((m) => ({ ...m, [matchId]: 'Tipp gespeichert.' }));

      await loadMatches();

    } catch (err) {

      setMessages((m) => ({

        ...m,

        [matchId]: err.response?.data?.message || 'Speichern fehlgeschlagen.',

      }));

    } finally {

      setSaving((s) => ({ ...s, [matchId]: false }));

    }

  };



  const saveAllOpen = async () => {

    const toSave = matches.filter(

      (m) => m.canTip && isValidScore(drafts[m._id]?.home) && isValidScore(drafts[m._id]?.away)

    );

    for (const m of toSave) {

      await submit(m._id);

    }

  };



  if (loading && matches.length === 0) {

    return (

      <div className="flex justify-center py-24">

        <span className="loading loading-spinner loading-lg text-primary" />

      </div>

    );

  }



  return (

    <div className="space-y-6">

      <PageHeader

        kicker="Tippspiel"

        title="Tippen"

        subtitle={

          openWithoutTip > 0

            ? `${openWithoutTip} offene${openWithoutTip === 1 ? 's' : ''} Spiel${openWithoutTip === 1 ? '' : 'e'} ohne Tipp`

            : 'Alle offenen Spiele getippt'

        }

        actions={(

          <>

            <button
              type="button"
              className={`btn-wacker-outline btn-wacker--sm ${showScoring ? 'border-primary text-primary' : ''}`}
              onClick={() => setShowScoring((v) => !v)}
              aria-expanded={showScoring}
              aria-controls="punkte-regeln"
            >
              {showScoring ? 'Regeln schließen' : 'Punkte-Regeln'}
            </button>

            {openWithoutTip > 0 && (

              <button type="button" className="btn-wacker btn-wacker--sm" onClick={saveAllOpen}>

                Alle speichern

              </button>

            )}

          </>

        )}

      />



      <ScoringInfo scoring={scoring} compact />



      {error && <div className="alert-error-dark">{error}</div>}
      <ScoringInfo
        scoring={scoring}
        open={showScoring}
        onClose={() => setShowScoring(false)}
      />



      <div className="space-y-3">

        {roundGroups.map((round) => {

          const isOpen = openRoundId === round.id;

          return (

            <div key={round.id} className={`round-accordion ${isOpen ? 'is-open' : ''}`}>

              <button

                type="button"

                className="round-accordion-trigger"

                onClick={() => setOpenRoundId(isOpen ? null : round.id)}

                aria-expanded={isOpen}

              >

                <div>

                  <div className="round-accordion-title">{round.name}</div>

                  <div className="round-accordion-sub">

                    {round.matches.length} Spiele

                    {round.missingTips > 0 && (

                      <span className="text-amber-400 ml-2">

                        · {round.missingTips} ohne Tipp

                      </span>

                    )}

                  </div>

                </div>

                <span className={`round-accordion-chevron ${isOpen ? 'is-open' : ''}`} aria-hidden>

                  ▾

                </span>

              </button>

              {isOpen && (

                <div className="round-accordion-body">

                  {round.matches.map((m) => (

                    <MatchTipCard

                      key={m._id}

                      match={m}

                      drafts={drafts}

                      setDrafts={setDrafts}

                      saving={saving}

                      messages={messages}

                      onSubmit={submit}

                    />

                  ))}

                </div>

              )}

            </div>

          );

        })}



        {roundGroups.length === 0 && (

          <div className="glass-card p-10 text-center">

            <p className="panel-title border-0 mb-2">Keine Runden</p>

            <p className="text-white/50">

              {matches.length === 0

                ? 'Keine offenen Spiele. Bitte Admin um Synchronisation.'

                : 'Aktuell keine bevorstehenden Spieltage zum Tippen.'}

            </p>

          </div>

        )}

      </div>

    </div>

  );

};



export default TippspielSubmit;


